import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Header, Segment } from 'semantic-ui-react';
import { v4 as uuid } from 'uuid';
import { LoadingComponent } from '../../../app/layout/LoadingComponent';
import { ActivityFormValues } from '../../../app/models/activity';
import { useStore } from '../../../app/stores/store';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { MyTextInput } from '../../../app/common/form/MyTextInput';
import { MyTextArea } from '../../../app/common/form/MyTextArea';
import { MySelectInput } from '../../../app/common/form/MySelectInput';
import { catergoryOptions } from '../../../app/common/options/categoryOptions';
import { MyDateInput } from '../../../app/common/form/MyDateInput';

const ActivityForm: React.FC = observer(() => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activityStore } = useStore();
  const { createActivity, updateActivity, loadActivity, loadingInitial } =
    activityStore;

  const [activity, setActivity] = useState<ActivityFormValues>(
    new ActivityFormValues(),
  );

  const validationSchema = Yup.object({
    title: Yup.string().required('The activity title is required'),
    description: Yup.string().required('The activity description is required'),
    category: Yup.string().required('The activity category is required'),
    date: Yup.string().required('The activity date is required').nullable(),
    city: Yup.string().required(),
    venue: Yup.string().required(),
  });

  useEffect(
    function onMount() {
      if (id) {
        loadActivity(id).then((activity) => {
          if (!activity) throw new Error('Activity not found');
          setActivity(new ActivityFormValues(activity));
        });
      }
    },
    [id, loadActivity],
  );

  function handleFormSubmit(activity: ActivityFormValues) {
    if (!activity.id) {
      const newActivity: ActivityFormValues = {
        ...activity,
        id: uuid(),
      };
      createActivity(newActivity).then(() =>
        navigate(`/activities/${newActivity.id}`),
      );
    } else {
      updateActivity(activity).then(() =>
        navigate(`/activities/${activity.id}`),
      );
    }
  }

  if (loadingInitial)
    return <LoadingComponent content="Loading activity ..." />;

  return (
    <Segment clearing>
      <Header content="Activity Details" sub color="teal" />
      <Formik
        enableReinitialize
        initialValues={activity}
        validationSchema={validationSchema}
        onSubmit={(activity: ActivityFormValues) => handleFormSubmit(activity)}
      >
        {({ handleSubmit, isValid, isSubmitting, dirty }) => (
          <Form className="ui form" onSubmit={handleSubmit} autoComplete="off">
            <MyTextInput name="title" placeholder="Title" />
            <MyTextArea placeholder="Description" name="description" rows={3} />
            <MySelectInput
              placeholder="Category"
              name="category"
              options={catergoryOptions}
            />
            <MyDateInput
              placeholderText="Date"
              name="date"
              showTimeSelect
              timeCaption="time"
              dateFormat="MMMM d, yyyy h:mm aa"
            />
            <Header content="Location Details" sub color="teal" />
            <MyTextInput placeholder="City" name="city" />
            <MyTextInput placeholder="Venue" name="venue" />
            <Button
              disabled={isSubmitting || !dirty || !isValid}
              loading={isSubmitting}
              floated="right"
              positive
              type="submit"
              content="Submit"
            />
            <Button
              as={Link}
              to="/activities"
              floated="right"
              type="button"
              content="Cancel"
            />
          </Form>
        )}
      </Formik>
    </Segment>
  );
});

export { ActivityForm };
